import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import useBlobAsUrl from 'editor/src/util/useBlobAsUrl';

import styles from './index.module.scss';

export interface PreviewItem {
  url?: string;
  blob?: Blob | null;
  name: string;
  thumbnailUrl?: string;
  spreadIndex?: number;
  scrollToActive?: boolean;
}

interface Props {
  item: PreviewItem;
  active: boolean;
  index: number;
  className?: string;
  loaderClassName?: string;
  disabled?: boolean;
  scrollToActive?: boolean;
  onSelect: (index: number) => void;
  children?: React.ReactNode;
}

function ListItem({
  item,
  active,
  index,
  className,
  loaderClassName,
  onSelect,
  children,
  disabled,
  scrollToActive = true,
}: Props) {
  const [loaded, setLoaded] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);

  const blobUrl = useBlobAsUrl(item.blob || null);
  const imageUrl = item.thumbnailUrl || item.url || blobUrl;

  const handleImageLoad = () => {
    setLoaded(true);
  };

  function onClick() {
    onSelect(index);
  }

  useEffect(() => {
    if (active && scrollToActive) {
      divRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [active, scrollToActive]);

  return (
    <div
      className={cn(styles.item, 'cy-list-item', className, {
        [styles.active]: active,
        'cy-active': active,
        [styles.loaded]: false,
        [styles.disabled]: disabled,
      })}
      onClick={onClick}
      ref={divRef}
    >
      <div
        className={cn(styles.image, {
          [loaderClassName || '']: !loaded,
          [styles.loaded]: loaded,
          'cy-loaded': loaded,
        })}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt={item.name}
            draggable={false}
            onLoad={handleImageLoad}
            style={{ display: loaded ? 'block' : 'none' }}
          />
        )}
      </div>
      {children}
    </div>
  );
}

export default React.memo(ListItem);
